import React, { useState, useEffect } from 'react'
import {
  Card, Table, Button, Tag, Input, Select, Tabs, Space,
  Tooltip, Progress, Row, Col,
} from 'antd'
import {
  EditOutlined, SearchOutlined, EyeOutlined, MailOutlined,
  PrinterOutlined, CheckCircleOutlined, CloseCircleOutlined,
  FileTextOutlined, WarningOutlined, FilePdfOutlined, CheckSquareOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { Invoice, invoiceStore } from './invoiceStore'
import { generateInvoiceHTML, printInvoicePDF, downloadInvoiceAsPDF, emailInvoice, stripAllocationRef, InvoicePdfData } from '@/utils/invoicePdfGenerator'
import { notifySuccess, notifyError } from '@/utils/notify'
import { useModulePermission } from '@/hooks/useModulePermission'
import { useMasterData, type BankAccount } from '@/context/MasterDataContext'
import { isInvoiceOverdue } from '@/utils/invoiceHelpers'
import numberToWords from 'number-to-words'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  draft:           { label: 'Draft',           color: '#8A93A6', bg: '#F1F4F9' },
  submitted:       { label: 'Submitted',       color: '#D97706', bg: '#FEF3C7' },
  rejected:        { label: 'Rejected',        color: '#DC2626', bg: '#FEE2E2' },
  issued:          { label: 'Issued',          color: '#2563EB', bg: '#EFF4FF' },
  partially_paid:  { label: 'Partial',         color: '#92400E', bg: '#FEF3C7' },
  paid:            { label: 'Paid',            color: '#065F46', bg: '#D1FAE5' },
  overdue:         { label: 'Overdue',         color: '#991B1B', bg: '#FEE2E2' },
  cancelled:       { label: 'Cancelled',       color: '#6B7280', bg: '#F3F4F6' },
}

const fmt = (n: number | string) => `₹${Number(n).toLocaleString('en-IN')}`

const BoolIcon = ({ v }: { v: boolean }) => v
  ? <CheckCircleOutlined style={{ color: '#059669', fontSize: 16 }} />
  : <CloseCircleOutlined style={{ color: '#E4E8F0', fontSize: 16 }} />

const n = (v: any): number => Number(v) || 0

const buildPdfData = (r: Invoice, bank?: BankAccount): InvoicePdfData => {
  const units      = n(r.units)
  const base       = n(r.baseAmount)
  const total      = n(r.totalAmount)
  const gst        = n(r.gstAmount)
  const igstAmt    = n(r.igstAmount)
  const cgstAmt    = n(r.cgstAmount) || (igstAmt > 0 ? 0 : gst / 2)
  const sgstAmt    = n(r.sgstAmount) || (igstAmt > 0 ? 0 : gst / 2)
  const tariff     = n(r.tariff) || (units ? base / units : 0)
  const cgstRate   = base ? Math.round((cgstAmt / base) * 100) : 9
  const sgstRate   = base ? Math.round((sgstAmt / base) * 100) : 9
  const igstRate   = base && igstAmt ? Math.round((igstAmt / base) * 100) : 18
  const rounded    = parseFloat((Math.round(total) - total).toFixed(2))
  return {
    invoiceNumber:  r.invoiceNumber,
    issueDate:      r.issueDate,
    dueDate:        r.dueDate,
    customerId:     r.customerRef ?? r.entityId ?? r.entity,
    clientName:     r.entity,
    clientAddress:  r.clientAddress ?? [],
    clientGstNo:    r.clientGstNo,
    // Same text shown as "Notes" on the invoice's own view page — keeps the
    // downloaded/printed PDF consistent with what's on screen instead of a
    // generic line that doesn't say what the invoice actually covers. The
    // internal allocation id is stripped — it's bookkeeping, not something
    // the invoice recipient needs to see.
    description:    r.notes ? stripAllocationRef(r.notes) : `Energy Supply — ${r.period}`,
    hsnSacCode:     r.sac ?? '998312',
    quantity:       units,
    ratePerUnit:    tariff,
    lineTotal:      base,
    cgstRate,
    cgstAmount:     cgstAmt,
    sgstRate,
    sgstAmount:     sgstAmt,
    igstRate,
    igstAmount:     igstAmt,
    subtotal:       base,
    roundedOff:     rounded,
    discount:       0,
    totalAmount:    total,
    amountInWords:  numberToWords.toWords(Math.round(total)).replace(/\b\w/g, c => c.toUpperCase()),
    bankName:          bank?.bankName,
    bankBranch:        bank?.branchName,
    bankAccountNumber: bank?.accountNumber,
    bankIfscCode:      bank?.ifscCode,
    bankSwiftCode:     bank?.swiftCode,
  }
}

const InvoiceList: React.FC = () => {
  const navigate = useNavigate()
  const { canEmail, canDownload } = useModulePermission('Finance')
  const { bankAccounts } = useMasterData()
  const defaultBank = bankAccounts.find(b => b.isDefault && b.status === 'active') || bankAccounts.find(b => b.status === 'active')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [typeTab, setTypeTab] = useState('all')
  const [invoices, setInvoices] = useState<Invoice[]>(() => invoiceStore.getAll())
  const [emailingId, setEmailingId] = useState<string | null>(null)

  const handleAction = (id: string, action: 'submit' | 'approve' | 'reject') => {
    let newStatus: Invoice['status'] = 'draft'
    if (action === 'submit') newStatus = 'submitted'
    if (action === 'approve') newStatus = 'issued'
    if (action === 'reject') newStatus = 'rejected'
    
    invoiceStore.save({ id, status: newStatus })
    setInvoices(invoiceStore.getAll())
  }

  // Hydrate from the DB on mount and stay live on any add/edit/status change —
  // without this, the list only ever shows whatever another page happened to
  // have already loaded into the shared in-memory cache (often nothing).
  useEffect(() => {
    const update = () => setInvoices(invoiceStore.getAll())
    update()
    invoiceStore.refresh().then(update)
    window.addEventListener(invoiceStore.eventName, update)
    return () => window.removeEventListener(invoiceStore.eventName, update)
  }, [])

  const totalIssued   = invoices.reduce((s, i) => s + (Number(i.totalAmount) || 0), 0)
  const totalPaid     = invoices.reduce((s, i) => s + (Number(i.paidAmount) || 0), 0)
  const outstanding   = totalIssued - totalPaid
  const overdue       = invoices.filter(isInvoiceOverdue).length
  const gstrPending   = invoices.filter(i => !i.gstr1Filed || !i.gstr3bFiled).length

  const kpiCards = [
    {
      label: 'Total Billed',
      value: fmt(totalIssued),
      icon: <FileTextOutlined style={{ fontSize: 24, color: 'var(--brand-primary)' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: 'var(--brand-primary)',
    },
    {
      label: 'Collected',
      value: fmt(totalPaid),
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: 'var(--brand-green)' }} />,
      iconBg: 'rgba(5,150,105,0.08)',
      borderColor: 'var(--brand-green)',
    },
    {
      label: 'Outstanding',
      value: fmt(outstanding),
      icon: <WarningOutlined style={{ fontSize: 24, color: 'var(--brand-energy)' }} />,
      iconBg: 'rgba(245,158,11,0.08)',
      borderColor: 'var(--brand-energy)',
    },
    {
      label: 'GSTR Pending',
      value: gstrPending,
      icon: <CloseCircleOutlined style={{ fontSize: 24, color: 'var(--brand-danger)' }} />,
      iconBg: 'rgba(220,38,38,0.08)',
      borderColor: 'var(--brand-danger)',
    },
  ]

  const filtered = invoices.filter(inv => {
    const matchType   = typeTab === 'all' || inv.type === typeTab
    const matchSearch = !search || inv.invoiceNumber.toLowerCase().includes(search.toLowerCase()) || inv.entity.toLowerCase().includes(search.toLowerCase())
    const matchStatus = !statusFilter || (statusFilter === 'overdue' ? isInvoiceOverdue(inv) : inv.status === statusFilter)
    return matchType && matchSearch && matchStatus
  })

  const columns = [
    {
      title: 'Invoice #',
      key: 'number',
      width: 170,
      render: (_: any, r: Invoice) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span
              style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
              onClick={() => navigate(`/invoices/${r.id}`)}
            >
              {r.invoiceNumber}
            </span>
            {r.type === 'generator'
              ? <span className="pill pill-brand" style={{ fontSize: 10, padding: '0 6px', flexShrink: 0 }}>Generator</span>
              : <span className="pill pill-info" style={{ fontSize: 10, padding: '0 6px', background: '#EFF4FF', color: '#2563EB', flexShrink: 0 }}>Consumer</span>}
          </div>
          {r.period && (
            <div style={{ fontSize: 11, color: '#8A93A6', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
              {r.period}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Entity',
      dataIndex: 'entity',
      width: 150,
      ellipsis: true,
      render: (e: string) => <span style={{ fontWeight: 600, fontSize: 13 }}>{e}</span>,
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 130,
      render: (_: any, r: Invoice) => (
        <div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13 }}>{fmt(r.totalAmount)}</div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>GST: {fmt(r.gstAmount)}</div>
        </div>
      ),
    },
    {
      title: 'Collection',
      key: 'collection',
      width: 100,
      render: (_: any, r: Invoice) => {
        const pct = r.totalAmount > 0 ? Math.round((r.paidAmount / r.totalAmount) * 100) : 0
        return (
          <div>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 700, color: pct === 100 ? '#059669' : '#92400E' }}>{pct}%</span>
            <Progress percent={pct} showInfo={false} strokeColor={pct === 100 ? '#059669' : '#F59E0B'} trailColor="#E4E8F0" size="small" style={{ margin: 0 }} />
          </div>
        )
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: any, r: Invoice) => {
        const cfg = statusConfig[r.status] || { label: r.status, color: '#6B7280', bg: '#F3F4F6' }
        return (
          <span className="pill" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
        )
      },
    },
    {
      title: 'Generated On',
      key: 'issueDate',
      width: 110,
      render: (_: any, r: Invoice) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#4A5568' }}>{r.issueDate}</span>
      ),
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      width: 110,
      render: (_: any, r: Invoice) => {
        const d = r.dueDate
        const isOverdue = isInvoiceOverdue(r)
        return (
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: isOverdue ? '#DC2626' : '#4A5568', fontWeight: isOverdue ? 700 : 400 }}>
            {isOverdue && <WarningOutlined style={{ marginRight: 4 }} />}{d}
          </span>
        )
      },
    },
    {
      title: 'Compliance',
      key: 'compliance',
      width: 110,
      align: 'center' as const,
      render: (_: any, r: Invoice) => (
        <Space size={6}>
          <Tooltip title={`IRN: ${r.irn ?? 'Not generated'}`}><BoolIcon v={!!r.irn} /></Tooltip>
          <Tooltip title={`GSTR-1: ${r.gstr1Filed ? 'Filed' : 'Pending'}`}><BoolIcon v={r.gstr1Filed} /></Tooltip>
          <Tooltip title={`GSTR-3B: ${r.gstr3bFiled ? 'Filed' : 'Pending'}`}><BoolIcon v={r.gstr3bFiled} /></Tooltip>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      fixed: 'right' as const,
      render: (_: any, r: Invoice) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/invoices/${r.id}`)} />
          </Tooltip>

          {(r.status === 'draft' || r.status === 'rejected') && (
            <Tooltip title="Edit Invoice">
              <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/invoices/${r.id}/edit`)} />
            </Tooltip>
          )}

          {(r.status === 'draft' || r.status === 'rejected') && (
            <Tooltip title="Submit for Approval">
              <Button size="small" icon={<CheckSquareOutlined />} onClick={() => handleAction(r.id, 'submit')} />
            </Tooltip>
          )}
          {r.status === 'submitted' && (
            <>
              <Tooltip title="Approve">
                <Button size="small" type="primary" style={{ backgroundColor: '#059669' }} icon={<CheckCircleOutlined />} onClick={() => handleAction(r.id, 'approve')} />
              </Tooltip>
              <Tooltip title="Reject">
                <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => handleAction(r.id, 'reject')} />
              </Tooltip>
            </>
          )}

          {canDownload && (
            <Tooltip title="Download PDF">
              <Button size="small" icon={<FilePdfOutlined />} onClick={() => downloadInvoiceAsPDF(buildPdfData(r, defaultBank), `${r.invoiceNumber}.pdf`)} />
            </Tooltip>
          )}
          <Tooltip title="Print Invoice">
            <Button size="small" icon={<PrinterOutlined />} onClick={() => printInvoicePDF(generateInvoiceHTML(buildPdfData(r, defaultBank)))} />
          </Tooltip>
          {canEmail && (
            <Tooltip title="Email Invoice">
              <Button
                size="small"
                icon={<MailOutlined />}
                loading={emailingId === r.id}
                onClick={async () => {
                  setEmailingId(r.id)
                  try {
                    const sentTo = await emailInvoice(r.id, buildPdfData(r, defaultBank))
                    notifySuccess('Invoice emailed', `Sent to ${sentTo}`)
                  } catch (err) {
                    notifyError('Could not email invoice', err instanceof Error ? err.message : undefined)
                  } finally {
                    setEmailingId(null)
                  }
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]

  const tabItems = [
    { key: 'all',       label: `All (${invoices.length})` },
    { key: 'consumer',  label: `Consumer (${invoices.filter(i => i.type === 'consumer').length})` },
    { key: 'generator', label: `Generator (${invoices.filter(i => i.type === 'generator').length})` },
  ]

  if (!invoiceStore.loaded) return <TableSkeleton columns={12} />

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title">Invoices</h2>
          <p className="page-subtitle">GST-compliant invoices for generators and consumers — IRN, e-Way Bill, GSTR export</p>
        </div>
        <Tag icon={<FileTextOutlined />} color="blue" style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, whiteSpace: 'normal', maxWidth: 280 }}>
          Draft invoices are generated automatically when an allocation is approved
        </Tag>
      </div>

      {/* KPI Cards */}
      <KpiCardRow cards={kpiCards} />

      {/* Table card */}
      <Card bodyStyle={{ padding: 0 }}>
        {/* antd's Card title+extra render as a single non-wrapping flex row —
            fine for a short title, but Tabs + a fixed-width search/filter
            pair fought for space on tablet (tabs got squeezed into a "..."
            overflow menu) and overflowed the viewport entirely on mobile
            (fixed px widths don't shrink). A normal Row/Col grid wraps
            properly at each breakpoint instead. */}
        <div style={{ padding: '12px 16px 0' }}>
          <Row gutter={[12, 12]} justify="space-between" align="middle">
            <Col xs={24} lg="auto" style={{ minWidth: 0 }}>
              <Tabs
                items={tabItems}
                activeKey={typeTab}
                onChange={setTypeTab}
                style={{ marginBottom: -1 }}
                size="small"
              />
            </Col>
            <Col xs={24} lg="auto">
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={14} md={16} lg={16}>
                  <Input
                    placeholder="Search invoice / entity..."
                    prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
                    style={{ width: '100%', minWidth: 200 }}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={10} md={8} lg={8}>
                  <Select
                    placeholder="Status"
                    allowClear
                    style={{ width: '100%', minWidth: 130 }}
                    value={statusFilter || undefined}
                    onChange={v => setStatusFilter(v ?? '')}
                    options={Object.entries(statusConfig).map(([v, c]) => ({ value: v, label: c.label }))}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: t => `${t} invoices` }}
          scroll={{ x: 1050 }}
          onRow={(record) => ({
            onClick: () => navigate(`/invoices/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* GSTR legend */}
      <div style={{ marginTop: 12, fontSize: 12, color: '#8A93A6', display: 'flex', gap: 20, padding: '0 4px' }}>
        <span>IRN = Invoice Reference Number (e-invoice)</span>
        <span>GSTR-1 = Monthly outward supply return</span>
        <span>GSTR-3B = Monthly summary return</span>
      </div>
    </div>
  )
}

export default InvoiceList
